import { v4 as uuidv4 } from 'uuid';
import { onCLS, onINP, onLCP, onFCP, onTTFB } from 'web-vitals';

// ============================================================
// MurmurHash3 — inline for zero-dependency SDK
// ============================================================

function murmurhash3(key, seed = 0x1234ABCD) {
    let h1 = seed >>> 0;
    const len = key.length;
    const nblocks = len >> 2;
    const c1 = 0xcc9e2d51;
    const c2 = 0x1b873593;

    for (let i = 0; i < nblocks; i++) {
        let k1 =
            (key.charCodeAt(i * 4) & 0xff) |
            ((key.charCodeAt(i * 4 + 1) & 0xff) << 8) |
            ((key.charCodeAt(i * 4 + 2) & 0xff) << 16) |
            ((key.charCodeAt(i * 4 + 3) & 0xff) << 24);
        k1 = Math.imul(k1, c1);
        k1 = (k1 << 15) | (k1 >>> 17);
        k1 = Math.imul(k1, c2);
        h1 ^= k1;
        h1 = (h1 << 13) | (h1 >>> 19);
        h1 = Math.imul(h1, 5) + 0xe6546b64;
    }

    let k1 = 0;
    const tail = nblocks * 4;
    switch (len & 3) {
        case 3: k1 ^= (key.charCodeAt(tail + 2) & 0xff) << 16;
        // falls through
        case 2: k1 ^= (key.charCodeAt(tail + 1) & 0xff) << 8;
        // falls through
        case 1:
            k1 ^= key.charCodeAt(tail) & 0xff;
            k1 = Math.imul(k1, c1);
            k1 = (k1 << 15) | (k1 >>> 17);
            k1 = Math.imul(k1, c2);
            h1 ^= k1;
    }

    h1 ^= len;
    h1 ^= h1 >>> 16;
    h1 = Math.imul(h1, 0x85ebca6b);
    h1 ^= h1 >>> 13;
    h1 = Math.imul(h1, 0xc2b2ae35);
    h1 ^= h1 >>> 16;
    return h1 >>> 0;
}

function getBucket(experimentKey, userId, totalBuckets = 10000) {
    return murmurhash3(`${experimentKey}:${userId}`) % totalBuckets;
}

// ============================================================
// Bot Detection
// ============================================================

const BOT_PATTERNS = /bot|crawl|spider|slurp|googlebot|bingbot|yandexbot|duckduckbot|baiduspider|facebookexternalhit|twitterbot|linkedinbot|whatsapp|telegrambot|applebot|semrush|ahrefs|mj12bot|dotbot|petalbot|bytespider|gptbot|claudebot|anthropic|chatgpt|headlesschrome|phantomjs|prerender|lighthouse|pagespeed|pingdom|uptimerobot|statuspage/i;

function isBot(userAgent) {
    if (!userAgent) return false;
    return BOT_PATTERNS.test(userAgent);
}

// ============================================================
// Gradient Tracker — Event Tracking + Experimentation SDK
// ============================================================

const TOTAL_BUCKETS = 10000;
const CONFIG_CACHE_KEY = '_gradient_config';
const CONFIG_TTL_MS = 5 * 60 * 1000; // 5 minutes

class GradientTracker {
    constructor(config = {}) {
        this.config = {
            apiEndpoint: config.apiEndpoint || 'http://localhost:3001/api/events/track',
            configEndpoint: config.configEndpoint || null, // Will be derived from apiEndpoint
            storeId: config.storeId || 'unknown',
            debug: config.debug || false,
            batchSize: config.batchSize || 10,
            batchInterval: config.batchInterval || 5000,
            configTtl: config.configTtl || CONFIG_TTL_MS,
            ...config
        };

        // Bot detection — skip all tracking for bots
        this._isBot = typeof navigator !== 'undefined' && isBot(navigator.userAgent);
        if (this._isBot) {
            if (this.config.debug) {
                console.log('Gradient: Bot detected, skipping initialization');
            }
            return;
        }

        // Derive config endpoint from API endpoint if not explicitly set
        if (!this.config.configEndpoint && this.config.apiEndpoint) {
            const base = this.config.apiEndpoint.replace(/\/events\/track$/, '');
            this.config.configEndpoint = `${base}/config/${this.config.storeId}`;
        }

        this.eventQueue = [];
        this.exposureQueue = [];
        this.sessionId = this.getOrCreateSessionId();
        this.userId = this.getUserId();

        // Consent state (null = not set, true = granted, false = denied)
        this._analyticsConsent = null;

        // Experimentation state
        this._configDoc = null;
        this._configFetchPromise = null;
        this._exposedExperiments = new Set(); // Deduplication per session
        this._overrides = {};                  // Manual overrides for testing

        // Variant application state (Phase 2D)
        this._appliedStyles = [];              // Injected <style> element refs
        this._mutationObserver = null;
        this._variantChanges = [];             // Active DOM changes for MutationObserver re-apply
        this._isApplyingChanges = false;       // Re-entrance guard for MutationObserver
        this._observerDebounceTimer = null;    // Debounce timer for observer callbacks
        this._isPreviewMode = false;
        this._previewOverrides = {};           // experimentKey → variantKey

        // Check preview mode before init
        this._checkPreviewMode();

        this.init();
    }

    init() {
        if (this.config.debug) {
            console.log('Gradient Tracker initialized', {
                storeId: this.config.storeId,
                sessionId: this.sessionId,
                userId: this.userId
            });
        }

        // Start batch processor
        this.startBatchProcessor();

        // Auto-track page views
        this.trackPageView();

        // Initialize page-level tracking (scroll depth + time-on-page)
        this._initPageTracking();

        // Initialize Web Vitals
        this._initWebVitals();

        // Track page visibility changes
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    this._trackPageViewEnd();
                    this.flush();
                    this._flushExposures();
                }
            });
        }

        // Track before unload
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                this._trackPageViewEnd();
                this.flush();
                this._flushExposures();
            });
        }

        // Fetch config document
        this._fetchConfig();
    }

    // ============================================================
    // Consent Management
    // ============================================================

    /**
     * Set analytics consent status. When consent is denied:
     * - Config still loads (needed for variant application)
     * - Variants still apply (visual, not tracking)
     * - Events are NOT tracked
     * - Exposures are NOT logged
     * @param {boolean} granted
     */
    setAnalyticsConsent(granted) {
        this._analyticsConsent = Boolean(granted);
        if (this.config.debug) {
            console.log('Gradient: Analytics consent', granted ? 'granted' : 'denied');
        }
    }

    /**
     * Check if tracking is allowed (consent not explicitly denied)
     */
    _canTrack() {
        return this._analyticsConsent !== false;
    }

    // ============================================================
    // Session & User Management
    // ============================================================

    getOrCreateSessionId() {
        const SESSION_KEY = '_gradient_session';
        const SESSION_DURATION = 30 * 60 * 1000;

        if (typeof localStorage === 'undefined') return uuidv4();

        try {
            const stored = localStorage.getItem(SESSION_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                // Support both old 'timestamp' and new 'lastActivity' field names
                const lastActivity = parsed.lastActivity || parsed.timestamp;
                if (Date.now() - lastActivity < SESSION_DURATION) {
                    // Don't update lastActivity on read — only _refreshSession() does that
                    return parsed.id;
                }
            }
        } catch (e) { /* localStorage might be disabled */ }

        const newSessionId = uuidv4();
        try {
            localStorage.setItem(SESSION_KEY, JSON.stringify({ id: newSessionId, lastActivity: Date.now() }));
        } catch (e) { /* Ignore */ }
        return newSessionId;
    }

    /**
     * Refresh session activity timestamp — called from track() so any
     * tracked event extends the session window.
     */
    _refreshSession() {
        const SESSION_KEY = '_gradient_session';
        if (typeof localStorage === 'undefined') return;
        try {
            localStorage.setItem(SESSION_KEY, JSON.stringify({ id: this.sessionId, lastActivity: Date.now() }));
        } catch (e) { /* Ignore */ }
    }

    getUserId() {
        const USER_KEY = '_gradient_user';
        if (typeof localStorage === 'undefined') return null;

        try {
            let userId = localStorage.getItem(USER_KEY);
            if (!userId) {
                userId = uuidv4();
                localStorage.setItem(USER_KEY, userId);
            }
            return userId;
        } catch (e) {
            return null;
        }
    }

    // ============================================================
    // Config Document — Precomputed flags/experiments from server
    // ============================================================

    async _fetchConfig() {
        if (!this.config.configEndpoint) return;

        // Check localStorage cache first (skip cache in preview mode for fresh config)
        if (!this._isPreviewMode) {
            try {
                const cached = localStorage.getItem(CONFIG_CACHE_KEY);
                if (cached) {
                    const { doc, fetchedAt } = JSON.parse(cached);
                    const ttl = (doc.ttlSeconds || 300) * 1000;
                    if (Date.now() - fetchedAt < Math.min(ttl, this.config.configTtl)) {
                        this._configDoc = doc;
                        if (this.config.debug) {
                            console.log('Config loaded from cache', doc);
                        }
                        this._onConfigReady();
                        return;
                    }
                }
            } catch (e) { /* Ignore */ }
        }

        // Fetch from server
        if (this._configFetchPromise) return this._configFetchPromise;

        this._configFetchPromise = (async () => {
            try {
                const response = await fetch(this.config.configEndpoint);
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const doc = await response.json();
                this._configDoc = doc;

                // Cache in localStorage
                try {
                    localStorage.setItem(CONFIG_CACHE_KEY, JSON.stringify({
                        doc,
                        fetchedAt: Date.now()
                    }));
                } catch (e) { /* Ignore */ }

                if (this.config.debug) {
                    console.log('Config fetched from server', doc);
                }

                this._onConfigReady();

                // Schedule next refresh
                setTimeout(() => {
                    this._configFetchPromise = null;
                    this._fetchConfig();
                }, (doc.ttlSeconds || 300) * 1000);
            } catch (error) {
                if (this.config.debug) {
                    console.warn('Failed to fetch config:', error);
                }
                this._configFetchPromise = null;
                // Remove anti-flicker even on failure
                this._removeAntiFlicker();
            }
        })();

        return this._configFetchPromise;
    }

    /**
     * Force refresh config from server (clears cache)
     */
    refreshConfig() {
        try { localStorage.removeItem(CONFIG_CACHE_KEY); } catch (e) { /* Ignore */ }
        this._configFetchPromise = null;
        return this._fetchConfig();
    }

    /**
     * Called after config document is loaded (from cache or server)
     */
    _onConfigReady() {
        // Handle URL split redirects first (must happen before page renders)
        if (this._handleUrlSplitRedirects()) return; // Page is redirecting

        // Handle Shopify template experiments (redirect via ?view= before page renders)
        if (this._handleTemplateExperiments()) return; // Page is redirecting

        const apply = () => this._applyActiveExperiments();
        if (typeof document !== 'undefined' && document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', apply);
        } else {
            apply();
        }
    }

    /**
     * Handle URL split experiment redirects.
     * Returns true if a redirect is happening (caller should stop processing).
     */
    _handleUrlSplitRedirects() {
        if (typeof window === 'undefined') return false;
        if (!this._configDoc || !this._configDoc.experiments) return false;

        const currentPath = window.location.pathname;
        const experiments = this._configDoc.experiments;

        for (const expKey of Object.keys(experiments)) {
            const exp = experiments[expKey];
            if (exp.type !== 'url_split' || exp.status !== 'running') continue;
            if (!exp.urlMatch) continue;

            // Check if current URL matches the trigger
            if (currentPath !== exp.urlMatch && !currentPath.startsWith(exp.urlMatch + '?')) continue;

            // Assign variant
            const result = this.getExperiment(expKey);
            if (!result.inExperiment) continue;

            // Find the URL for the assigned variant
            const variant = exp.variants.find(v => v.key === result.variant);
            if (!variant || !variant.url) continue;

            // If we're already on the variant's URL, don't redirect
            if (currentPath === variant.url) continue;

            // Redirect to variant URL, preserving query params
            let redirectUrl = variant.url;
            if (window.location.search) {
                const sep = redirectUrl.includes('?') ? '&' : '?';
                redirectUrl += sep + window.location.search.substring(1);
            }

            if (this.config.debug) {
                console.log(`Gradient: URL split redirect to ${redirectUrl} (${expKey}:${result.variant})`);
            }

            window.location.replace(redirectUrl);
            return true;
        }

        return false;
    }

    /**
     * Handle Shopify template experiment redirects.
     * Uses Shopify's native ?view= parameter for server-side template rendering.
     * Returns true if a redirect is happening.
     */
    _handleTemplateExperiments() {
        if (typeof window === 'undefined') return false;
        if (!this._configDoc || !this._configDoc.experiments) return false;

        // If already on a ?view= URL, don't redirect again (prevent loops)
        const params = new URLSearchParams(window.location.search);
        if (params.has('view')) return false;

        // Map Shopify page types to URL path patterns
        const currentPath = window.location.pathname;
        const pageType = this._detectShopifyPageType(currentPath);

        const experiments = this._configDoc.experiments;

        for (const expKey of Object.keys(experiments)) {
            const exp = experiments[expKey];
            if (exp.type !== 'shopify_template' || exp.status !== 'running') continue;

            const templateConfig = exp.shopifyTemplateConfig;
            if (!templateConfig) continue;

            // Check if current page matches the template type
            if (templateConfig.templateType !== pageType) continue;

            // Assign variant via normal bucketing
            const result = this.getExperiment(expKey);
            if (!result.inExperiment) continue;

            // Control group sees the default template (no redirect needed)
            if (result.variant === 'control') continue;

            // Extract template suffix for ?view= parameter
            // e.g. "templates/product.gradient-yln11p.json" → "gradient-yln11p"
            const suffix = this._extractTemplateSuffix(templateConfig.variantTemplate, templateConfig.templateType);
            if (!suffix) continue;

            // Build redirect URL with ?view= parameter
            const url = new URL(window.location.href);
            url.searchParams.set('view', suffix);

            if (this.config.debug) {
                console.log(`Gradient: Template redirect ?view=${suffix} (${expKey}:${result.variant})`);
            }

            window.location.replace(url.toString());
            return true;
        }

        return false;
    }

    /**
     * Detect Shopify page type from URL path.
     * Returns: 'index', 'product', 'collection', 'page', 'blog', 'article', 'cart', 'search', 'list-collections', or null.
     */
    _detectShopifyPageType(path) {
        if (path === '/') return 'index';
        if (/^\/products\/[^/]+/.test(path)) return 'product';
        if (path === '/collections' || path === '/collections/') return 'list-collections';
        if (/^\/collections\/[^/]+/.test(path)) return 'collection';
        if (/^\/blogs\/[^/]+\/[^/]+/.test(path)) return 'article';
        if (/^\/blogs\/[^/]+\/?$/.test(path)) return 'blog';
        if (/^\/pages\/[^/]+/.test(path)) return 'page';
        if (path === '/cart') return 'cart';
        if (path.startsWith('/search')) return 'search';
        return null;
    }

    /**
     * Extract template suffix from variant template key.
     * "templates/product.gradient-yln11p.json" → "gradient-yln11p"
     * "templates/index.gradient-abc123.json" → "gradient-abc123"
     */
    _extractTemplateSuffix(variantTemplate, templateType) {
        // Remove "templates/" prefix and ".json" suffix
        const name = variantTemplate.replace('templates/', '').replace('.json', '');
        // Remove the template type prefix (e.g. "product." → "")
        const prefix = templateType + '.';
        if (name.startsWith(prefix)) {
            return name.substring(prefix.length);
        }
        return name;
    }

    /**
     * Wait for config to be available (for async initialization)
     */
    async waitForConfig() {
        if (this._configDoc) return;
        if (this._configFetchPromise) await this._configFetchPromise;
    }

    // ============================================================
    // Feature Gates
    // ============================================================

    /**
     * Check a feature gate (boolean flag)
     * @param {string} gateName
     * @returns {boolean}
     */
    checkGate(gateName) {
        if (!this._configDoc || !this._configDoc.featureGates) return false;

        const gate = this._configDoc.featureGates[gateName];
        if (!gate) return false;

        // Evaluate targeting rules
        if (gate.rules && gate.rules.length > 0) {
            const result = this._evaluateRules(gate.rules);
            if (result !== undefined) return Boolean(result);
        }

        return gate.value;
    }

    // ============================================================
    // Experiments
    // ============================================================

    /**
     * Get experiment variant assignment
     * @param {string} experimentKey
     * @returns {{ variant: string, payload: object, inExperiment: boolean }}
     */
    getExperiment(experimentKey) {
        const defaultResult = { variant: 'control', payload: {}, inExperiment: false };

        // Check preview overrides first (from URL param), then manual overrides
        const overrideKey = this._previewOverrides[experimentKey] || this._overrides[experimentKey];
        if (overrideKey) {
            // Resolve payload from config doc
            const payload = this._resolveVariantPayload(experimentKey, overrideKey);
            return {
                variant: overrideKey,
                payload,
                inExperiment: true
            };
        }

        if (!this._configDoc || !this._configDoc.experiments) return defaultResult;

        const experiment = this._configDoc.experiments[experimentKey];
        if (!experiment || experiment.status !== 'running') return defaultResult;
        if (!experiment.variants || experiment.variants.length === 0) return defaultResult;

        // Check targeting rules
        if (experiment.targeting && experiment.targeting.length > 0) {
            const result = this._evaluateRules(experiment.targeting);
            if (result === false) return defaultResult;
        }

        // Check traffic allocation
        const trafficBucket = getBucket(`traffic:${experimentKey}`, this.userId, TOTAL_BUCKETS);
        const trafficThreshold = experiment.trafficAllocation * (TOTAL_BUCKETS / 100);
        if (trafficBucket >= trafficThreshold) return defaultResult;

        // Assign variant deterministically
        const variant = this._assignVariant(experimentKey, experiment.variants);

        // Log exposure (deduplicated per session)
        if (!this._exposedExperiments.has(experimentKey)) {
            this._exposedExperiments.add(experimentKey);
            this._logExposure(experimentKey, variant.key);
        }

        return {
            variant: variant.key,
            payload: variant.payload || {},
            inExperiment: true
        };
    }

    /**
     * Deterministic variant assignment via murmurhash
     */
    _assignVariant(experimentKey, variants) {
        const bucket = getBucket(experimentKey, this.userId, TOTAL_BUCKETS);
        const totalWeight = variants.reduce((sum, v) => sum + v.weight, 0);
        const scale = totalWeight > 0 ? TOTAL_BUCKETS / totalWeight : 1;

        let cumulative = 0;
        for (const variant of variants) {
            cumulative += variant.weight * scale;
            if (bucket < cumulative) return variant;
        }

        return variants[variants.length - 1];
    }

    /**
     * Set manual override for an experiment (for QA/testing)
     */
    overrideExperiment(experimentKey, variantKey) {
        this._overrides[experimentKey] = variantKey;
    }

    /**
     * Clear all manual overrides
     */
    clearOverrides() {
        this._overrides = {};
    }

    // ============================================================
    // Dynamic Configs
    // ============================================================

    /**
     * Get a dynamic config value
     * @param {string} configKey
     * @returns {object}
     */
    getConfig(configKey) {
        if (!this._configDoc || !this._configDoc.dynamicConfigs) return {};

        const dynConfig = this._configDoc.dynamicConfigs[configKey];
        if (!dynConfig) return {};

        if (dynConfig.rules && dynConfig.rules.length > 0) {
            const result = this._evaluateRules(dynConfig.rules);
            if (result !== undefined && typeof result === 'object') return result;
        }

        return dynConfig.value || {};
    }

    // ============================================================
    // Targeting Rule Evaluation
    // ============================================================

    _evaluateRules(rules) {
        const context = this._getEvaluationContext();

        for (const rule of rules) {
            // Check percentage gate
            if (rule.percentage !== undefined) {
                const bucket = getBucket(rule.id, this.userId, TOTAL_BUCKETS);
                if (bucket >= rule.percentage * 100) continue;
            }

            // Check all conditions (AND)
            const allMatch = rule.conditions.every(cond => this._evaluateCondition(cond, context));
            if (allMatch) return rule.returnValue;
        }

        return undefined;
    }

    _evaluateCondition(condition, context) {
        const value = context[condition.property] || (context.custom && context.custom[condition.property]);

        switch (condition.operator) {
            case 'eq': return value === condition.value;
            case 'neq': return value !== condition.value;
            case 'gt': return typeof value === 'number' && value > condition.value;
            case 'gte': return typeof value === 'number' && value >= condition.value;
            case 'lt': return typeof value === 'number' && value < condition.value;
            case 'lte': return typeof value === 'number' && value <= condition.value;
            case 'contains': return typeof value === 'string' && value.toLowerCase().includes(String(condition.value).toLowerCase());
            case 'not_contains': return typeof value === 'string' && !value.toLowerCase().includes(String(condition.value).toLowerCase());
            case 'in': return Array.isArray(condition.value) && condition.value.includes(value);
            case 'not_in': return Array.isArray(condition.value) && !condition.value.includes(value);
            case 'percentage': {
                const bucket = getBucket(condition.property, this.userId, TOTAL_BUCKETS);
                return bucket < (typeof condition.value === 'number' ? condition.value : 100) * 100;
            }
            default: return false;
        }
    }

    _getEvaluationContext() {
        const deviceInfo = this.getDeviceInfo();
        return {
            userId: this.userId,
            sessionId: this.sessionId,
            deviceType: deviceInfo.deviceType,
            device_type: deviceInfo.deviceType,
            browser: deviceInfo.browser,
            os: deviceInfo.os,
            country: null, // Set from server-side enrichment in future
        };
    }

    // ============================================================
    // Exposure Tracking
    // ============================================================

    _logExposure(experimentKey, variantKey) {
        // Suppress exposure logging in preview mode or when consent denied
        if (this._isPreviewMode) return;
        if (!this._canTrack()) return;

        const deviceInfo = this.getDeviceInfo();
        this.exposureQueue.push({
            storeId: this.config.storeId,
            experimentKey,
            variantKey,
            userId: this.userId,
            sessionId: this.sessionId,
            deviceType: deviceInfo.deviceType,
            country: null,
            firstExposure: true,
            timestamp: new Date().toISOString()
        });

        // Also track as a regular event for analytics
        this.track('experiment_exposure', {
            experimentKey,
            variantKey
        });
    }

    async _flushExposures() {
        if (this.exposureQueue.length === 0) return;

        const exposuresToSend = [...this.exposureQueue];
        this.exposureQueue = [];

        try {
            const endpoint = this.config.apiEndpoint.replace(/\/events\/track$/, '/exposures/batch');
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(exposuresToSend),
                keepalive: true
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            if (this.config.debug) {
                console.log(`Flushed ${exposuresToSend.length} exposures`);
            }
        } catch (error) {
            if (this.config.debug) {
                console.warn('Failed to send exposures:', error);
            }
            // Re-queue
            if (this.exposureQueue.length < 100) {
                this.exposureQueue.unshift(...exposuresToSend);
            }
        }
    }

    // ============================================================
    // Device Info
    // ============================================================

    getDeviceInfo() {
        if (typeof navigator === 'undefined') {
            return { deviceType: 'unknown', browser: 'unknown', os: 'unknown' };
        }

        const ua = navigator.userAgent;
        let deviceType = 'desktop';
        if (/mobile/i.test(ua)) deviceType = 'mobile';
        else if (/tablet|ipad/i.test(ua)) deviceType = 'tablet';

        return {
            deviceType,
            browser: this.getBrowser(ua),
            os: this.getOS(ua),
            screenWidth: typeof window !== 'undefined' ? window.screen.width : null,
            screenHeight: typeof window !== 'undefined' ? window.screen.height : null
        };
    }

    getBrowser(ua) {
        if (ua.includes('Chrome')) return 'Chrome';
        if (ua.includes('Safari')) return 'Safari';
        if (ua.includes('Firefox')) return 'Firefox';
        if (ua.includes('Edge')) return 'Edge';
        return 'Unknown';
    }

    getOS(ua) {
        if (ua.includes('Win')) return 'Windows';
        if (ua.includes('Mac')) return 'MacOS';
        if (ua.includes('Linux')) return 'Linux';
        if (ua.includes('Android')) return 'Android';
        if (ua.includes('iOS')) return 'iOS';
        return 'Unknown';
    }

    // ============================================================
    // Event Tracking (existing functionality preserved)
    // ============================================================

    track(eventType, properties = {}) {
        // Skip tracking when consent is denied
        if (!this._canTrack()) return;

        // Refresh session on every tracked event (rolling window)
        this._refreshSession();

        const deviceInfo = this.getDeviceInfo();

        const event = {
            storeId: this.config.storeId,
            sessionId: this.sessionId,
            userId: this.userId,
            eventType,
            eventName: properties.eventName || eventType,
            properties,
            pageUrl: typeof window !== 'undefined' ? window.location.href : null,
            pageTitle: typeof document !== 'undefined' ? document.title : null,
            referrer: typeof document !== 'undefined' ? (document.referrer || null) : null,
            deviceType: deviceInfo.deviceType,
            browser: deviceInfo.browser,
            os: deviceInfo.os,
            screenWidth: deviceInfo.screenWidth,
            screenHeight: deviceInfo.screenHeight,
            timestamp: new Date().toISOString()
        };

        this.eventQueue.push(event);

        if (this.config.debug) {
            console.log('Event tracked:', event);
        }

        if (this.eventQueue.length >= this.config.batchSize) {
            this.flush();
        }
    }

    trackPageView() {
        if (typeof window === 'undefined') return;
        const utm = this._parseUTM();
        this.track('page_view', {
            path: window.location.pathname,
            search: window.location.search,
            hash: window.location.hash,
            ...utm,
        });

        // Auto-track search queries on search results pages
        this._trackSearchIfApplicable();
    }

    // ============================================================
    // Search Tracking
    // ============================================================

    /**
     * Auto-detect Shopify search queries from URL (/search?q=...) and track them.
     * Called automatically on page view if on a search results page.
     */
    _trackSearchIfApplicable() {
        if (typeof window === 'undefined') return;
        try {
            const path = window.location.pathname;
            const params = new URLSearchParams(window.location.search);
            const query = params.get('q') || params.get('query');
            if ((path === '/search' || path.startsWith('/search')) && query) {
                // Count results on page (Shopify search results use .product-card or similar)
                const resultCount = document.querySelectorAll('[class*="product-card"], [class*="search-result"], .grid__item').length;
                this.track('search', {
                    query,
                    path,
                    result_count: resultCount,
                });
            }
        } catch (e) {
            // Silently fail
        }
    }

    // ============================================================
    // UTM Parsing
    // ============================================================

    _parseUTM() {
        if (typeof window === 'undefined') return {};
        try {
            const params = new URLSearchParams(window.location.search);
            const utm = {};
            const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
            for (const key of utmKeys) {
                const value = params.get(key);
                if (value) utm[key] = value;
            }
            return Object.keys(utm).length > 0 ? utm : {};
        } catch (e) {
            return {};
        }
    }

    // ============================================================
    // Page Tracking: scroll depth + time-on-page
    // ============================================================

    _initPageTracking() {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        this._pageLoadTime = Date.now();
        this._maxScrollDepth = 0;
        this._pageViewEndFired = false;

        // Track max scroll depth via passive scroll listener
        const onScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const docHeight = Math.max(
                document.body.scrollHeight,
                document.documentElement.scrollHeight
            );
            const winHeight = window.innerHeight;
            const scrollableHeight = docHeight - winHeight;
            if (scrollableHeight > 0) {
                const depth = Math.min(100, Math.round((scrollTop / scrollableHeight) * 100));
                if (depth > this._maxScrollDepth) {
                    this._maxScrollDepth = depth;
                }
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
    }

    _trackPageViewEnd() {
        if (this._pageViewEndFired) return;
        if (typeof window === 'undefined') return;
        this._pageViewEndFired = true;

        const timeOnPageSeconds = Math.round((Date.now() - (this._pageLoadTime || Date.now())) / 1000);

        this.track('page_view_end', {
            path: window.location.pathname,
            scrollDepth: this._maxScrollDepth || 0,
            timeOnPageSeconds,
        });
    }

    // ============================================================
    // Web Vitals
    // ============================================================

    _initWebVitals() {
        if (typeof window === 'undefined') return;

        const reportVital = (metric) => {
            this.track('web_vital', {
                name: metric.name,
                value: metric.value,
                rating: metric.rating,
                delta: metric.delta,
            });
        };

        try {
            onCLS(reportVital);
            onINP(reportVital);
            onLCP(reportVital);
            onFCP(reportVital);
            onTTFB(reportVital);
        } catch (e) {
            if (this.config.debug) {
                console.warn('Web Vitals initialization failed:', e);
            }
        }
    }

    trackClick(element, properties = {}) {
        this.track('click', {
            elementType: element.tagName,
            elementId: element.id,
            elementClass: element.className,
            elementText: element.textContent?.substring(0, 100),
            ...properties
        });
    }

    trackAddToCart(product) {
        this.track('add_to_cart', {
            productId: product.id,
            productName: product.name,
            price: product.price,
            quantity: product.quantity || 1
        });
    }

    trackRemoveFromCart(product) {
        this.track('remove_from_cart', {
            productId: product.id,
            productName: product.name
        });
    }

    trackCheckoutStarted(cart) {
        this.track('checkout_started', {
            itemCount: cart.itemCount,
            totalValue: cart.totalValue
        });
    }

    trackPurchase(order) {
        this.track('purchase_completed', {
            orderId: order.id,
            totalValue: order.total,
            itemCount: order.itemCount,
            items: order.items
        });
    }

    // ============================================================
    // Preview Mode (Phase 2D)
    // ============================================================

    /**
     * Parse ?gradient_preview=experimentKey:variantKey[,exp2:var2] from URL
     */
    _checkPreviewMode() {
        if (typeof window === 'undefined') return;
        try {
            const params = new URLSearchParams(window.location.search);
            const preview = params.get('gradient_preview');
            if (!preview) return;

            this._isPreviewMode = true;
            for (const pair of preview.split(',')) {
                const [expKey, varKey] = pair.split(':');
                if (expKey && varKey) {
                    this._previewOverrides[expKey.trim()] = varKey.trim();
                }
            }

            if (this.config.debug) {
                console.log('Gradient preview mode active:', this._previewOverrides);
            }
        } catch (e) { /* Ignore */ }
    }

    /**
     * Inject floating preview control panel into the page
     */
    _showPreviewBadge() {
        if (typeof document === 'undefined') return;
        if (document.getElementById('gradient-preview-panel')) return;

        const experiments = this._configDoc?.experiments || {};
        const previewKeys = Object.keys(this._previewOverrides);
        if (previewKeys.length === 0) return;

        // Build variant buttons for each previewed experiment
        let panelContent = '';
        for (const expKey of previewKeys) {
            const exp = experiments[expKey];
            if (!exp) continue;
            const currentVariant = this._previewOverrides[expKey];
            const variants = exp.variants || [];
            const variantButtons = variants.map(v => {
                const isActive = v.key === currentVariant;
                const style = isActive
                    ? 'background:#7c3aed;color:#fff;'
                    : 'background:#f3f4f6;color:#374151;';
                return `<button data-gradient-switch="${expKey}:${v.key}" style="${style}border:none;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:11px;font-family:system-ui,sans-serif;">${v.name || v.key}</button>`;
            }).join('');

            // Add "Original" button to clear variant
            const origActive = !currentVariant;
            const origStyle = origActive
                ? 'background:#7c3aed;color:#fff;'
                : 'background:#f3f4f6;color:#374151;';
            const origButton = `<button data-gradient-switch="${expKey}:__original__" style="${origStyle}border:none;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:11px;font-family:system-ui,sans-serif;">Original</button>`;

            panelContent += `
                <div style="margin-bottom:8px;">
                    <div style="font-size:11px;color:#9ca3af;margin-bottom:4px;">${exp.name || expKey}</div>
                    <div style="display:flex;flex-wrap:wrap;gap:4px;">${origButton}${variantButtons}</div>
                </div>`;
        }

        const panel = document.createElement('div');
        panel.id = 'gradient-preview-panel';
        panel.innerHTML = `
            <div id="gradient-preview-inner" style="position:fixed;bottom:16px;right:16px;z-index:999999;background:#fff;border:1px solid #e5e7eb;border-radius:12px;box-shadow:0 8px 24px rgba(0,0,0,0.15);font-family:system-ui,sans-serif;min-width:220px;max-width:320px;">
                <div style="display:flex;align-items:center;justify-content:space-between;padding:10px 14px;border-bottom:1px solid #f3f4f6;cursor:move;" id="gradient-preview-drag">
                    <span style="font-size:12px;font-weight:600;color:#7c3aed;">&#x2207; Preview Mode</span>
                    <div style="display:flex;gap:4px;">
                        <button id="gradient-preview-collapse" style="background:none;border:none;cursor:pointer;font-size:14px;color:#9ca3af;padding:0;">&#x2015;</button>
                        <button id="gradient-preview-exit" style="background:none;border:none;cursor:pointer;font-size:14px;color:#9ca3af;padding:0;">&times;</button>
                    </div>
                </div>
                <div id="gradient-preview-body" style="padding:12px 14px;">
                    ${panelContent}
                </div>
            </div>`;
        document.body.appendChild(panel);

        // Variant switching
        panel.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-gradient-switch]');
            if (!btn) return;
            const [switchExp, switchVar] = btn.getAttribute('data-gradient-switch').split(':');

            if (switchVar === '__original__') {
                delete this._previewOverrides[switchExp];
            } else {
                this._previewOverrides[switchExp] = switchVar;
            }

            // Update URL without reload
            const params = new URLSearchParams(window.location.search);
            const previewPairs = Object.entries(this._previewOverrides).map(([k, v]) => `${k}:${v}`);
            if (previewPairs.length > 0) {
                params.set('gradient_preview', previewPairs.join(','));
            } else {
                params.delete('gradient_preview');
                this._isPreviewMode = false;
            }
            const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
            window.history.replaceState(null, '', newUrl);

            // Re-apply variants
            this._cleanupVariants();
            this._applyActiveExperiments();
        });

        // Collapse/expand
        const collapseBtn = document.getElementById('gradient-preview-collapse');
        const body = document.getElementById('gradient-preview-body');
        if (collapseBtn && body) {
            collapseBtn.addEventListener('click', () => {
                body.style.display = body.style.display === 'none' ? 'block' : 'none';
                collapseBtn.textContent = body.style.display === 'none' ? '+' : '\u2015';
            });
        }

        // Exit preview
        const exitBtn = document.getElementById('gradient-preview-exit');
        if (exitBtn) {
            exitBtn.addEventListener('click', () => {
                const params = new URLSearchParams(window.location.search);
                params.delete('gradient_preview');
                const newUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
                window.location.replace(newUrl);
            });
        }

        // Draggable
        const dragHandle = document.getElementById('gradient-preview-drag');
        const innerPanel = document.getElementById('gradient-preview-inner');
        if (dragHandle && innerPanel) {
            let isDragging = false;
            let startX, startY, startRight, startBottom;
            dragHandle.addEventListener('mousedown', (e) => {
                isDragging = true;
                startX = e.clientX;
                startY = e.clientY;
                const rect = innerPanel.getBoundingClientRect();
                startRight = window.innerWidth - rect.right;
                startBottom = window.innerHeight - rect.bottom;
                e.preventDefault();
            });
            document.addEventListener('mousemove', (e) => {
                if (!isDragging) return;
                innerPanel.style.right = (startRight - (e.clientX - startX)) + 'px';
                innerPanel.style.bottom = (startBottom - (e.clientY - startY)) + 'px';
            });
            document.addEventListener('mouseup', () => { isDragging = false; });
        }
    }

    /**
     * Remove all applied variant styles and DOM changes
     */
    _cleanupVariants() {
        // Remove injected styles
        for (const style of this._appliedStyles) {
            if (style.parentNode) style.parentNode.removeChild(style);
        }
        this._appliedStyles = [];

        // Stop MutationObserver
        if (this._mutationObserver) {
            this._mutationObserver.disconnect();
            this._mutationObserver = null;
        }
        this._variantChanges = [];

        // Remove preview panel for re-render
        const panel = document.getElementById('gradient-preview-panel');
        if (panel) panel.remove();
    }

    // ============================================================
    // Variant Application Engine (Phase 2D)
    // ============================================================

    /**
     * Resolve variant payload from config document
     */
    _resolveVariantPayload(experimentKey, variantKey) {
        if (!this._configDoc || !this._configDoc.experiments) return {};
        const exp = this._configDoc.experiments[experimentKey];
        if (!exp || !exp.variants) return {};
        const variant = exp.variants.find(v => v.key === variantKey);
        return (variant && variant.payload) || {};
    }

    /**
     * Resolve which experiments are eligible, respecting mutual exclusion layers.
     * Returns a Set of experiment keys that should be evaluated.
     */
    _resolveEligibleExperiments() {
        const eligible = new Set();
        const experiments = this._configDoc.experiments;
        const layers = this._configDoc.layers || {};

        // Track which experiments are in layers
        const layeredExperiments = new Set();
        for (const [layerId, expKeys] of Object.entries(layers)) {
            for (const key of expKeys) layeredExperiments.add(key);

            // Pick one experiment from this layer via deterministic hash
            const layerBucket = getBucket(`layer:${layerId}`, this.userId, TOTAL_BUCKETS);

            // Divide bucket space proportionally among layer experiments
            const activeInLayer = expKeys.filter(k =>
                experiments[k] && (experiments[k].status === 'running' ||
                    (this._isPreviewMode && this._previewOverrides[k]))
            );
            if (activeInLayer.length === 0) continue;

            const totalAllocation = activeInLayer.reduce(
                (sum, k) => sum + (experiments[k].trafficAllocation || 100), 0
            );
            let cumulative = 0;
            let selected = activeInLayer[activeInLayer.length - 1]; // fallback
            for (const key of activeInLayer) {
                cumulative += ((experiments[key].trafficAllocation || 100) / totalAllocation) * TOTAL_BUCKETS;
                if (layerBucket < cumulative) {
                    selected = key;
                    break;
                }
            }
            eligible.add(selected);
        }

        // All non-layered experiments are independently eligible
        for (const expKey of Object.keys(experiments)) {
            if (!layeredExperiments.has(expKey)) {
                eligible.add(expKey);
            }
        }

        return eligible;
    }

    /**
     * Apply all active experiment variants to the page
     */
    _applyActiveExperiments() {
        if (typeof document === 'undefined') return;
        if (!this._configDoc || !this._configDoc.experiments) {
            this._removeAntiFlicker();
            return;
        }

        // Kill switch: check gradient_experiments_kill feature flag
        if (this.checkGate('gradient_experiments_kill')) {
            if (this.config.debug) {
                console.log('Gradient variant application killed by feature flag');
            }
            this._removeAntiFlicker();
            return;
        }

        const experiments = this._configDoc.experiments;
        const eligible = this._resolveEligibleExperiments();
        let anyApplied = false;

        // Process redirects first (before DOM changes)
        for (const expKey of eligible) {
            const isPreviewOverride = this._isPreviewMode && this._previewOverrides[expKey];
            if (!isPreviewOverride && experiments[expKey].status !== 'running') continue;
            if (!this._matchesPageTargeting(expKey)) continue;

            const result = this.getExperiment(expKey);
            if (!result.inExperiment || !result.payload || !result.payload.changes) continue;

            for (const change of result.payload.changes) {
                if (change.type === 'redirect') {
                    this._applyRedirect(change);
                    return; // Stop processing — page is redirecting
                }
            }
        }

        // Apply DOM changes
        for (const expKey of eligible) {
            const isPreviewOverride = this._isPreviewMode && this._previewOverrides[expKey];
            if (!isPreviewOverride && experiments[expKey].status !== 'running') continue;
            if (!this._matchesPageTargeting(expKey)) continue;

            const result = this.getExperiment(expKey);
            if (!result.inExperiment || !result.payload || !result.payload.changes) continue;

            for (const change of result.payload.changes) {
                try {
                    this._applyChange(change);
                    anyApplied = true;
                } catch (err) {
                    if (this.config.debug) {
                        console.warn(`Failed to apply change for ${expKey}:`, err);
                    }
                }
            }
        }

        // Start MutationObserver for dynamic content re-application
        if (this._variantChanges.length > 0) {
            this._startMutationObserver();
        }

        // Remove anti-flicker
        this._removeAntiFlicker();

        // Dispatch event for integrations
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('gradient:variants-applied', {
                detail: { applied: anyApplied }
            }));
        }

        // Show preview badge after variants applied
        if (this._isPreviewMode) {
            this._showPreviewBadge();
        }

        if (this.config.debug) {
            console.log('Gradient variants applied:', anyApplied);
        }
    }

    /**
     * Check if experiment's page targeting matches current URL
     */
    _matchesPageTargeting(experimentKey) {
        if (!this._configDoc || !this._configDoc.experiments) return false;
        const exp = this._configDoc.experiments[experimentKey];
        if (!exp) return false;

        // Get payload to check page targeting — need to resolve variant
        const overrideKey = this._previewOverrides[experimentKey] || this._overrides[experimentKey];
        let payload;
        if (overrideKey) {
            payload = this._resolveVariantPayload(experimentKey, overrideKey);
        } else {
            // Peek at any variant's payload for pageTargeting (shared across variants)
            const firstVariant = exp.variants && exp.variants[0];
            payload = (firstVariant && firstVariant.payload) || {};
        }

        if (!payload.pageTargeting) return true; // No targeting = matches all pages

        const loc = window.location;
        const { urlPattern, pathPrefix } = payload.pageTargeting;

        if (pathPrefix && !loc.pathname.startsWith(pathPrefix)) return false;
        if (urlPattern) {
            try {
                const regex = new RegExp(urlPattern);
                if (!regex.test(loc.pathname + loc.search)) return false;
            } catch (e) { return false; }
        }

        return true;
    }

    /**
     * Apply a single change to the DOM
     */
    _applyChange(change) {
        switch (change.type) {
            case 'css': return this._applyCss(change);
            case 'text': return this._applyText(change);
            case 'visibility': return this._applyVisibility(change);
            case 'class': return this._applyClass(change);
            case 'attribute': return this._applyAttribute(change);
            case 'html': return this._applyHtml(change);
            case 'custom_js': return this._applyCustomJs(change);
            // redirect handled separately in _applyActiveExperiments
        }
    }

    /**
     * Inject a <style> tag for CSS changes (auto-applies to dynamic content)
     */
    _applyCss(change) {
        const rules = Object.entries(change.properties)
            .map(([prop, val]) => `${prop}:${val} !important`)
            .join(';');
        const css = `${change.selector}{${rules}}`;
        const style = document.createElement('style');
        style.setAttribute('data-gradient-variant', 'true');
        style.textContent = css;
        document.head.appendChild(style);
        this._appliedStyles.push(style);
    }

    /**
     * Replace text content of matched elements
     */
    _applyText(change) {
        const elements = document.querySelectorAll(change.selector);
        elements.forEach(el => {
            if (change.mode === 'html') {
                if (el.innerHTML !== change.content) {
                    el.innerHTML = change.content;
                }
            } else {
                if (el.textContent !== change.content) {
                    el.textContent = change.content;
                }
            }
        });
        // Store for MutationObserver re-application (only on first apply, not re-apply)
        if (!this._isApplyingChanges) {
            this._variantChanges.push(change);
        }
    }

    /**
     * Show/hide elements via <style> tag (auto-applies to dynamic content)
     */
    _applyVisibility(change) {
        const display = change.action === 'hide' ? 'none' : 'revert';
        const css = `${change.selector}{display:${display} !important}`;
        const style = document.createElement('style');
        style.setAttribute('data-gradient-variant', 'true');
        style.textContent = css;
        document.head.appendChild(style);
        this._appliedStyles.push(style);
    }

    /**
     * Add/remove CSS classes on matched elements
     */
    _applyClass(change) {
        const elements = document.querySelectorAll(change.selector);
        elements.forEach(el => {
            if (change.add) el.classList.add(...change.add);
            if (change.remove) el.classList.remove(...change.remove);
        });
        if (!this._isApplyingChanges) {
            this._variantChanges.push(change);
        }
    }

    /**
     * Set/remove attributes on matched elements
     */
    _applyAttribute(change) {
        const elements = document.querySelectorAll(change.selector);
        elements.forEach(el => {
            for (const [attr, val] of Object.entries(change.attributes)) {
                if (val === null) {
                    el.removeAttribute(attr);
                } else {
                    if (el.getAttribute(attr) !== val) {
                        el.setAttribute(attr, val);
                    }
                }
            }
        });
        if (!this._isApplyingChanges) {
            this._variantChanges.push(change);
        }
    }

    /**
     * Replace/prepend/append HTML content
     */
    _applyHtml(change) {
        const elements = document.querySelectorAll(change.selector);
        elements.forEach(el => {
            switch (change.position) {
                case 'replace':
                    el.innerHTML = change.html;
                    break;
                case 'prepend':
                    el.insertAdjacentHTML('afterbegin', change.html);
                    break;
                case 'append':
                    el.insertAdjacentHTML('beforeend', change.html);
                    break;
            }
        });
    }

    /**
     * Redirect to a different URL
     */
    _applyRedirect(change) {
        let url = change.url;
        if (change.preserveQuery && window.location.search) {
            const sep = url.includes('?') ? '&' : '?';
            url += sep + window.location.search.substring(1);
        }
        window.location.replace(url);
    }

    /**
     * Execute custom JavaScript (escape hatch for Phase 3A)
     */
    _applyCustomJs(change) {
        try {
            new Function(change.code)();
        } catch (err) {
            if (this.config.debug) {
                console.warn('Gradient custom_js error:', err);
            }
        }
    }

    /**
     * Remove anti-flicker class and style
     */
    _removeAntiFlicker() {
        if (typeof document === 'undefined') return;
        document.documentElement.classList.remove('gradient-loading');
        const style = document.getElementById('gradient-anti-flicker');
        if (style) style.remove();
    }

    /**
     * MutationObserver: re-apply text/class/attribute changes when DOM nodes are added
     * (for Shopify AJAX cart, section rendering, etc.)
     */
    _startMutationObserver() {
        if (this._mutationObserver || typeof MutationObserver === 'undefined') return;

        this._mutationObserver = new MutationObserver((mutations) => {
            // Skip if we're currently applying changes (re-entrance guard)
            if (this._isApplyingChanges) return;

            let hasNewNodes = false;
            for (const mutation of mutations) {
                // Ignore mutations from our own style/variant injections
                if (mutation.target && mutation.target.hasAttribute &&
                    mutation.target.hasAttribute('data-gradient-variant')) continue;
                if (mutation.addedNodes.length > 0) {
                    hasNewNodes = true;
                    break;
                }
            }
            if (!hasNewNodes) return;

            // Debounce: batch rapid DOM changes into a single re-application
            clearTimeout(this._observerDebounceTimer);
            this._observerDebounceTimer = setTimeout(() => {
                this._isApplyingChanges = true;
                try {
                    for (const change of this._variantChanges) {
                        try {
                            this._applyChange(change);
                        } catch (e) { /* Ignore re-application errors */ }
                    }
                } finally {
                    this._isApplyingChanges = false;
                }
            }, 50);
        });

        this._mutationObserver.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    // ============================================================
    // Batch Processing & Flush
    // ============================================================

    startBatchProcessor() {
        setInterval(() => {
            if (this.eventQueue.length > 0) this.flush();
            if (this.exposureQueue.length > 0) this._flushExposures();
        }, this.config.batchInterval);
    }

    async flush() {
        if (this.eventQueue.length === 0) return;

        const eventsToSend = [...this.eventQueue];
        this.eventQueue = [];

        try {
            const response = await fetch(this.config.apiEndpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(eventsToSend),
                keepalive: true
            });

            if (!response.ok) throw new Error(`HTTP ${response.status}`);

            if (this.config.debug) {
                const data = await response.json();
                console.log(`Flushed ${eventsToSend.length} events:`, data);
            }
        } catch (error) {
            console.error('Failed to send events:', error);
            if (this.eventQueue.length < 100) {
                this.eventQueue.unshift(...eventsToSend);
            }
        }
    }
}

// ============================================================
// Auto-initialization
// ============================================================

if (typeof window !== 'undefined') {
    window.GradientTracker = GradientTracker;
    window.GradientTracker.isBot = isBot;

    // Auto-init from script tag data attributes (only once)
    if (!window.gradient) {
        const script = document.currentScript;
        if (script && script.dataset.storeId) {
            window.gradient = new GradientTracker({
                storeId: script.dataset.storeId,
                apiEndpoint: script.dataset.apiEndpoint,
                configEndpoint: script.dataset.configEndpoint,
                debug: script.dataset.debug === 'true'
            });
        }
    }
}

export { isBot };
export default GradientTracker;
